import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Loader2, CheckCircle2, AlertTriangle, Building2, UserPlus, Layers } from 'lucide-react';
import { useOnboardClient } from '../../hooks/api/admin';
import { ProjectCatalogManager } from './ProjectCatalogManager';

function genTemporaryPassword() {
  const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  let out = '';
  for (let i = 0; i < 14; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out + '!9';
}

function suggestNextCode(existingCount) {
  // C01..C99 then C100, C101, ...
  const n = (existingCount || 0) + 1;
  return n < 100 ? `C${String(n).padStart(2, '0')}` : `C${n}`;
}

export function ClientOnboarding({ existingClientCount = 0, onSuccess }) {
  // ---- State ----
  const [client, setClient] = useState({
    code: suggestNextCode(existingClientCount),
    name: '',
    company_name: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    country: '',
    contract_start: '',
    contract_end: '',
    contract_tier: 'enterprise',
    status: 'active',
  });
  const [admin, setAdmin] = useState({
    full_name: '',
    email: '',
    temporary_password: genTemporaryPassword(),
  });
  const [selectedProjects, setSelectedProjects] = useState(new Set());
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // ---- Data ----
  const onboard = useOnboardClient();

  // ---- Helpers ----
  const setC = (k, v) => setClient((s) => ({ ...s, [k]: v }));
  const setA = (k, v) => setAdmin((s) => ({ ...s, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Basic client-side checks (server re-validates).
    // Code must follow the C0n format (e.g. C01, C02, …) or be empty
    // (auto-generated server-side).
    if (client.code && !/^C\d+$/i.test(client.code)) {
      setError('Client code must follow the C0n format, e.g. C01.');
      return;
    }
    if (!client.name || !admin.full_name || !admin.email || !admin.temporary_password) {
      setError('Fill all required fields (marked *).');
      return;
    }
    if (client.contract_end && client.contract_start &&
        new Date(client.contract_end) <= new Date(client.contract_start)) {
      setError('Contract end must be after contract start.');
      return;
    }

    try {
      const res = await onboard.mutateAsync({
        client: {
          ...client,
          contract_start: client.contract_start || null,
          contract_end: client.contract_end || null,
        },
        admin,
        project_ids: Array.from(selectedProjects),
      });
      setSuccess(res);
      onSuccess?.(res);
    } catch (err) {
      // Surface every validation error if the server returned a list, so the
      // operator can see exactly which field failed instead of a generic
      // "Onboarding failed".
      const errs = err?.details?.errors;
      if (Array.isArray(errs) && errs.length > 0) {
        const lines = errs.map((e) => {
          const path = (e.loc || []).slice(1).join('.') || 'request';
          return `• ${path}: ${e.msg}`;
        });
        setError(lines.join('\n'));
      } else {
        setError(err?.message || `Onboarding failed (status ${err?.status || '?'})`);
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Onboard New Client</h1>
          <p className="text-sm text-muted-foreground">
            Provision tenant, admin user, and module access in a single step.
          </p>
        </div>
        <Badge variant="outline">Super Admin Only</Badge>
      </header>

      {error && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          <AlertTriangle className="w-4 h-4 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="flex items-start gap-2 rounded-md border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm text-emerald-300">
          <CheckCircle2 className="w-4 h-4 mt-0.5" />
          <div className="space-y-1">
            <div>Client <strong>{success.client.code}</strong> provisioned.</div>
            <div className="text-xs opacity-90">
              Admin: <code>{success.admin_email}</code> · S3 prefix:{' '}
              <code>{success.s3_prefix}</code> · Projects:{' '}
              {success.enabled_project_ids.length}
            </div>
          </div>
        </div>
      )}

      {/* Section 1 — Client */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Building2 className="w-4 h-4" /> Client Details
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Client Code *">
            <Input value={client.code} onChange={(e) => setC('code', e.target.value.toUpperCase())} placeholder="CL_001" />
          </Field>
          <Field label="Client Name *">
            <Input value={client.name} onChange={(e) => setC('name', e.target.value)} placeholder="Akin Demo Energy" />
          </Field>
          <Field label="Company / Legal Entity">
            <Input value={client.company_name} onChange={(e) => setC('company_name', e.target.value)} placeholder="Akin Energy Pvt. Ltd." />
          </Field>
          <Field label="Contract Tier">
            <Select value={client.contract_tier} onValueChange={(v) => setC('contract_tier', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="trial">Trial</SelectItem>
                <SelectItem value="starter">Starter</SelectItem>
                <SelectItem value="pro">Pro</SelectItem>
                <SelectItem value="enterprise">Enterprise</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Contact Person">
            <Input value={client.contact_name} onChange={(e) => setC('contact_name', e.target.value)} />
          </Field>
          <Field label="Contact Email">
            <Input type="email" value={client.contact_email} onChange={(e) => setC('contact_email', e.target.value)} />
          </Field>
          <Field label="Contact Phone">
            <Input value={client.contact_phone} onChange={(e) => setC('contact_phone', e.target.value)} />
          </Field>
          <Field label="Country">
            <Input value={client.country} onChange={(e) => setC('country', e.target.value)} placeholder="India" />
          </Field>
          <Field label="Address" className="md:col-span-2">
            <Textarea rows={2} value={client.address} onChange={(e) => setC('address', e.target.value)} />
          </Field>
          <Field label="Contract Start">
            <Input type="date" value={client.contract_start} onChange={(e) => setC('contract_start', e.target.value)} />
          </Field>
          <Field label="Contract End">
            <Input type="date" value={client.contract_end} onChange={(e) => setC('contract_end', e.target.value)} />
          </Field>
          <Field label="Status">
            <Select value={client.status} onValueChange={(v) => setC('status', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="trial">Trial</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </CardContent>
      </Card>

      {/* Section 2 — Admin user */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <UserPlus className="w-4 h-4" /> Client Admin Account
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Full Name *">
            <Input value={admin.full_name} onChange={(e) => setA('full_name', e.target.value)} />
          </Field>
          <Field label="Email *">
            <Input type="email" value={admin.email} onChange={(e) => setA('email', e.target.value)} />
          </Field>
          <Field label="Temporary Password *" className="md:col-span-2">
            <div className="flex gap-2">
              <Input value={admin.temporary_password} onChange={(e) => setA('temporary_password', e.target.value)} />
              <Button type="button" variant="outline" onClick={() => setA('temporary_password', genTemporaryPassword())}>
                Regenerate
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              The user must change this on first login. (Forced-rotation hook is queued for a follow-up.)
            </p>
          </Field>
        </CardContent>
      </Card>

      {/* Section 3 — Project catalog (industries → projects → deliverables)
          Pick from the master catalog or click "+ Add" inline. New
          projects/deliverables get auto-coded PRJ_NNN / DEL_NNN unless you
          type an explicit code. */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Layers className="w-4 h-4" /> Project Catalog
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ProjectCatalogManager
            mode="select"
            clientCode={client.code || '<client>'}
            selectedProjectIds={selectedProjects}
            onSelectionChange={setSelectedProjects}
          />
          {selectedProjects.size > 0 && (
            <div className="mt-3 text-xs text-muted-foreground">
              {selectedProjects.size} project(s) will be enabled for this client.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Submit */}
      <div className="flex items-center justify-end gap-2">
        <Button type="submit" disabled={onboard.isPending}>
          {onboard.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          Provision Client
        </Button>
      </div>
    </form>
  );
}

function Field({ label, children, className = '' }) {
  return (
    <div className={className}>
      <Label className="text-xs uppercase tracking-wide text-muted-foreground mb-1 block">
        {label}
      </Label>
      {children}
    </div>
  );
}
